import type { ManagedStoreRow, RolesByCatalogStore } from "./types";

/** One catalog slice shared by multiple managed `store_id`s (same Margaritaville role list + group/concept). */
export interface CatalogSliceGroup {
  group: string;
  concept: string;
  /** Representative location label from catalog (first store in the group). */
  locationSample: string;
  roles: string[];
  storeIds: string[];
  businessNames: string[];
}

function sortedRoleSignature(roles: string[]): string {
  return [...roles].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" })).join("\x1e");
}

/**
 * Cluster managed stores so locations that share the same catalog slice (group + concept + role list)
 * appear once in the UI / summary CSV — avoids repeating the same role list for many restaurants.
 */
export function groupManagedStoresByCatalogSlice(
  rows: ManagedStoreRow[],
  roleByStoreId: Map<string, RolesByCatalogStore>
): CatalogSliceGroup[] {
  const buckets = new Map<string, CatalogSliceGroup>();

  for (const row of rows) {
    const r = roleByStoreId.get(row.store_id);
    if (!r) {
      const sig = `__pending__${row.store_id}`;
      buckets.set(sig, {
        group: "",
        concept: "",
        locationSample: row.business_name,
        roles: [],
        storeIds: [row.store_id],
        businessNames: [row.business_name]
      });
      continue;
    }

    const sig = `${r.group}\x00${r.concept}\x00${sortedRoleSignature(r.roles)}`;
    let g = buckets.get(sig);
    if (!g) {
      g = {
        group: r.group,
        concept: r.concept,
        locationSample: r.location,
        roles: [...r.roles],
        storeIds: [],
        businessNames: []
      };
      buckets.set(sig, g);
    }
    g.storeIds.push(row.store_id);
    g.businessNames.push(row.business_name);
  }

  return [...buckets.values()].sort((a, b) => {
    const ca = (a.concept || a.group).toLowerCase();
    const cb = (b.concept || b.group).toLowerCase();
    if (ca !== cb) return ca.localeCompare(cb);
    return a.storeIds[0].localeCompare(b.storeIds[0]);
  });
}

function csvEscapeCell(value: string): string {
  const flat = String(value).replace(/\r\n|\n|\r/g, " ").trim();
  if (/[",]/.test(flat)) {
    return `"${flat.replace(/"/g, '""')}"`;
  }
  return flat;
}

/** Grouped summary: one row per catalog slice; `store_ids` uses semicolons to avoid clashing with commas in role names. */
export function buildManagedStoresGroupedSummaryCsv(groups: CatalogSliceGroup[]): string {
  const header = ["group", "concept", "location_sample", "roles_pipe_separated", "store_count", "store_ids_semicolon"];
  const lines = [header.join(",")];
  for (const g of groups) {
    const rolesPipe = g.roles.join("|");
    const ids = g.storeIds.join(";");
    lines.push(
      [
        csvEscapeCell(g.group),
        csvEscapeCell(g.concept),
        csvEscapeCell(g.locationSample),
        csvEscapeCell(rolesPipe),
        String(g.storeIds.length),
        csvEscapeCell(ids)
      ].join(",")
    );
  }
  return `\uFEFF${lines.join("\r\n")}`;
}

export function buildManagedStoresFlatListCsv(rows: ManagedStoreRow[], roleByStoreId: Map<string, RolesByCatalogStore>): string {
  const header = ["store_id", "business_name", "group", "concept", "location"];
  const lines = [header.join(",")];
  for (const row of rows) {
    const r = roleByStoreId.get(row.store_id);
    lines.push(
      [
        csvEscapeCell(row.store_id),
        csvEscapeCell(row.business_name),
        csvEscapeCell(r?.group ?? ""),
        csvEscapeCell(r?.concept ?? ""),
        csvEscapeCell(r?.location ?? "")
      ].join(",")
    );
  }
  return `\uFEFF${lines.join("\r\n")}`;
}

export function triggerCsvDownload(content: string, filename: string): void {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
